if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.bindMainSwipe();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      /* Sabab-style desktop click-drag on main image; touch uses native scroll-snap */
      bindMainSwipe() {
        const viewer = this.elements.viewer;
        if (!viewer) return;
        const slider = viewer.querySelector('[id^="Slider-"]');
        if (!slider || slider.dataset.meteamoSwipeBound === 'true') return;
        slider.dataset.meteamoSwipeBound = 'true';

        const DRAG_THRESHOLD = 6;
        let dragging = false;
        let moved = false;
        let startX = 0;
        let startScroll = 0;
        let pointerId = null;

        const slides = () =>
          Array.from(slider.querySelectorAll('[id^="Slide-"]')).filter((el) => el.clientWidth > 0);

        const nearestSlide = () => {
          const items = slides();
          if (!items.length) return null;
          const center = slider.scrollLeft + slider.clientWidth / 2;
          let best = items[0];
          let bestDist = Infinity;
          for (const item of items) {
            const mid = item.offsetLeft + item.clientWidth / 2;
            const dist = Math.abs(mid - center);
            if (dist < bestDist) {
              bestDist = dist;
              best = item;
            }
          }
          return best;
        };

        const snapTo = (item, behavior = 'smooth') => {
          if (!item) return;
          slider.scrollTo({ left: item.offsetLeft, behavior });
          slides().forEach((el) => el.classList.remove('is-active'));
          item.classList.add('is-active');
        };

        const onPointerMove = (event) => {
          if (!dragging || (pointerId !== null && event.pointerId !== pointerId)) return;
          const dx = event.clientX - startX;
          if (Math.abs(dx) > DRAG_THRESHOLD) moved = true;
          if (!moved) return;
          event.preventDefault();
          slider.scrollLeft = startScroll - dx;
        };

        const onPointerUp = (event) => {
          if (!dragging) return;
          if (pointerId !== null && event.pointerId !== pointerId) return;
          dragging = false;
          slider.classList.remove('is-dragging');
          try {
            if (pointerId !== null && slider.hasPointerCapture?.(pointerId)) {
              slider.releasePointerCapture(pointerId);
            }
          } catch (e) {}
          document.removeEventListener('pointermove', onPointerMove, true);
          document.removeEventListener('pointerup', onPointerUp, true);
          document.removeEventListener('pointercancel', onPointerUp, true);
          pointerId = null;

          const target = nearestSlide();
          snapTo(target, 'smooth');

          if (moved) {
            const suppress = (ev) => {
              ev.preventDefault();
              ev.stopPropagation();
            };
            slider.addEventListener('click', suppress, { capture: true, once: true });
          }
        };

        // Mouse / pen only — leave touch to native scroll-snap (matches Sabab)
        // Prevent native <img> drag-ghost from stealing the gesture (Sabab uses preventDefault on mousedown)
        slider.querySelectorAll('img').forEach((img) => {
          img.setAttribute('draggable', 'false');
          img.draggable = false;
        });

        slider.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'touch') return;
          if (event.button !== 0) return;
          if (!(event.target instanceof Element)) return;
          const interactive = event.target.closest('a, input, model-viewer, .slider-button, button');
          if (interactive && !interactive.classList.contains('product__media-toggle')) return;
          if (slides().length <= 1) return;

          event.preventDefault();
          dragging = true;
          moved = false;
          startX = event.clientX;
          startScroll = slider.scrollLeft;
          pointerId = event.pointerId;
          slider.classList.add('is-dragging');
          try {
            slider.setPointerCapture(event.pointerId);
          } catch (e) {}
          document.addEventListener('pointermove', onPointerMove, true);
          document.addEventListener('pointerup', onPointerUp, true);
          document.addEventListener('pointercancel', onPointerUp, true);
        });

        // Fallback for environments that emit mouse events without pointer events
        slider.addEventListener('mousedown', (event) => {
          if (window.PointerEvent) return; // pointer path handles modern browsers
          if (event.button !== 0) return;
          if (!(event.target instanceof Element)) return;
          const interactive = event.target.closest('a, input, model-viewer, .slider-button, button');
          if (interactive && !interactive.classList.contains('product__media-toggle')) return;
          if (slides().length <= 1) return;
          event.preventDefault();
          dragging = true;
          moved = false;
          startX = event.clientX;
          startScroll = slider.scrollLeft;
          slider.classList.add('is-dragging');
          const onMove = (event2) => {
            const dx = event2.clientX - startX;
            if (Math.abs(dx) > DRAG_THRESHOLD) moved = true;
            if (!moved) return;
            event2.preventDefault();
            slider.scrollLeft = startScroll - dx;
          };
          const onUp = () => {
            dragging = false;
            slider.classList.remove('is-dragging');
            document.removeEventListener('mousemove', onMove, true);
            document.removeEventListener('mouseup', onUp, true);
            snapTo(nearestSlide(), 'smooth');
            if (moved) {
              const suppress = (ev) => {
                ev.preventDefault();
                ev.stopPropagation();
              };
              slider.addEventListener('click', suppress, { capture: true, once: true });
            }
          };
          document.addEventListener('mousemove', onMove, true);
          document.addEventListener('mouseup', onUp, true);
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
          }
          
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
